"use client";

import * as React from "react";
import { CalendarIcon, Clock, ClockIcon, X } from "lucide-react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { ScrollArea, ScrollBar } from "../ui/scroll-area";

type ScheduleInputProps = {
  date: Date | undefined;
  setDate: (date: Date | undefined) => void;
};

export function ScheduleInput({ date, setDate }: ScheduleInputProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const { t } = useTranslation();

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      const newDate = new Date(selectedDate);
      // Preserve existing time if we have a date
      if (date) {
        newDate.setHours(date.getHours());
        newDate.setMinutes(date.getMinutes());
      } else {
        // Set default time to current hour and 0 minutes
        const now = new Date();
        newDate.setHours(now.getHours());
        newDate.setMinutes(0);
      }
      setDate(newDate);
      // Keep the popover open after date selection
      setIsOpen(true);
    } else {
      setDate(undefined);
      setIsOpen(false);
    }
  };

  const handleTimeChange = (type: "hour" | "minute", value: string) => {
    if (date) {
      const newDate = new Date(date);
      if (type === "hour") {
        newDate.setHours(parseInt(value));
      } else if (type === "minute") {
        newDate.setMinutes(parseInt(value));
      }
      setDate(newDate);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <div className="relative">
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full justify-start text-left font-normal mt-2",
              !date && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date ? format(date, "MM/dd/yyyy") : <span>{t("schedule.select_date")}</span>}
            <ClockIcon className="mr-2 h-4 w-4" />
            {date ? format(date, "HH:mm") : t("schedule.select_time")}
          </Button>
        </PopoverTrigger>
        <X
          className="absolute right-4 top-[55%] -translate-y-1/2 h-5 w-5 cursor-pointer text-gray-400 hover:text-gray-700 transition-colors z-10"
          onClick={(e) => {
            e.stopPropagation();
            setDate(undefined);
            setIsOpen(false);
          }}
        />
      </div>
      <PopoverContent
        className="w-auto p-0"
        style={{ zIndex: 50 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sm:flex">
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleDateSelect}
            initialFocus
            className="rounded-md border"
            classNames={{
              nav_button: cn(
                "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 focus:opacity-100 focus:ring-2 focus:ring-primary"
              ),
              day: cn(
                "h-9 w-9 p-2 font-normal aria-selected:opacity-100 focus:ring-2 focus:ring-primary rounded-md"
              ),
            }}
          />
          <div className="flex flex-col sm:flex-row sm:h-[300px] divide-y sm:divide-y-0 sm:divide-x">
            <div className="flex flex-col">
              <div className="p-2 text-center font-medium text-sm text-gray-500 border-b">
                {t("schedule.hour")}
              </div>
              <ScrollArea className="w-64 sm:w-auto">
                <div className="flex sm:flex-col p-2">
                  {hours.reverse().map((hour) => (
                    <Button
                      key={hour}
                      size="icon"
                      variant={
                        date && date.getHours() === hour ? "default" : "ghost"
                      }
                      className="sm:w-full shrink-0 aspect-square text-lg focus:ring-2 focus:ring-primary"
                      onClick={() => handleTimeChange("hour", hour.toString())}
                    >
                      {hour}
                    </Button>
                  ))}
                </div>
                <ScrollBar orientation="horizontal" className="sm:hidden" />
              </ScrollArea>
            </div>
            <div className="flex flex-col">
              <div className="p-2 text-center font-medium text-sm text-gray-500 border-b">
                {t("schedule.minute")}
              </div>
              <ScrollArea className="w-64 sm:w-auto">
                <div className="flex sm:flex-col p-2">
                  {Array.from({ length: 60 }, (_, i) => i).map((minute) => (
                    <Button
                      key={minute}
                      size="icon"
                      variant={
                        date && date.getMinutes() === minute
                          ? "default"
                          : "ghost"
                      }
                      className="sm:w-full shrink-0 aspect-square text-lg focus:ring-2 focus:ring-primary"
                      onClick={() =>
                        handleTimeChange("minute", minute.toString())
                      }
                    >
                      {minute.toString().padStart(2, "0")}
                    </Button>
                  ))}
                </div>
                <ScrollBar orientation="horizontal" className="sm:hidden" />
              </ScrollArea>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
